import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, tap } from "rxjs";
import { UserTransfer } from "../models/user.model";
import { AuthenticationService } from "./auth.service";
import { Client, ClientTransfer } from "../models/client.model";
import { ViaCep } from "../interfaces/via-cep.interface";
import { ListResponse } from "../interfaces/list-response.interface";
import { DiceTransfer, RequestFormResponse, RequestModel } from "../interfaces/request.interface";
import { CheckEmail } from "../interfaces/check-email.interface";

@Injectable({
  providedIn: "root"
})
export class ApiServiceClient {
  private optionsHeader = {
    headers: new HttpHeaders({
      "Content-Type": "application/json; charset=UTF-8",
      "origin": "https://app.teddy360.com.br",
      "x-tenant-id": "1",
      "tenantUuuid": "51105888-88bc-4795-98c2-73c1de5a7c25",
      Authorization: this.authenticationService.getBearerToken()
    })
  };

  private optionsHeaderFile = {
    headers: new HttpHeaders({
      "origin": "https://app.teddy360.com.br",
      "x-tenant-id": "1",
      "tenantUuuid": "51105888-88bc-4795-98c2-73c1de5a7c25",
      Authorization: this.authenticationService.getBearerToken()
    })
  };

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService
  ) { }

  getAllClients() {
    return this.http.get<ListResponse<Client[]>>(`/clients?total_page=999999'`, this.optionsHeader).pipe(
      tap(res => {
        if (res.list) {
          res.list.forEach(item => {
            if (!item.cnpj == null) {
              let cnpjCpf = item.cnpj.replace(/\D/g, "");

              if (cnpjCpf.length === 11) {
                item.cnpj = cnpjCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4");
              } else if (cnpjCpf.length === 14) {
                item.cnpj = cnpjCpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "$1.$2.$3/$4-$5");
              }
            }
          });
        }
      }),
      tap(res => {
        res.list.forEach(item => {
          let getDateTime = item.created_at.split("T")[0];
          let getDate = getDateTime.split(".")[0].split("-");
          let formatDate = getDate[2] + "/" + getDate[1] + "/" + getDate[0];
          item.created_at = formatDate;
        });
      })
    );
  }

  getAllClientsStatus(status?: string) {
    return this.http
      .get<ListResponse<Client[]>>(`/clients?status=${status}&total_page=999999'`, this.optionsHeader)
      .pipe(
        tap(res => {
          if (res.list) {
            res.list.forEach(item => {
              if (!item.cnpj == null) {
                let cnpjCpf = item.cnpj.replace(/\D/g, "");

                if (cnpjCpf.length === 11) {
                  item.cnpj = cnpjCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4");
                } else if (cnpjCpf.length === 14) {
                  item.cnpj = cnpjCpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "$1.$2.$3/$4-$5");
                }
              }
            });
          }
        }),
        tap(res => {
          res.list.forEach(item => {
            let getDateTime = item.created_at.split("T")[0];
            let getDate = getDateTime.split(".")[0].split("-");
            let formatDate = getDate[2] + "/" + getDate[1] + "/" + getDate[0];
            item.created_at = formatDate;
          });
        })
      );
  }

  getByIdClient(id: string) {
    return this.http.get<Client>(`/clients/${id}`, this.optionsHeader);
  }

  createClient(data: Client) {
    return this.http.post<{ uuid: string }>(`/clients`, data, this.optionsHeader);
  }

  updateClient(id: string, data: Client) {
    return this.http.put<void>(`/clients/${id}/edit`, data, this.optionsHeader);
  }

  getAllRequest(idClient: string, order: string = null) {
    return this.http.get<ListResponse<RequestModel[]>>(
      `/clients/${idClient}/requests?total_page=999999'${order ? "&order=" + order : ""}`,
      this.optionsHeader
    );
  }

  getByIdRequest(idClient: string, idRequest: string) {
    return this.http.get<RequestFormResponse>(`/clients/${idClient}/requests/${idRequest}`, this.optionsHeader);
  }

  createRequest(data: any) {
    return this.http.post<{ uuid: string }>(`/clients/requests`, data, this.optionsHeader);
  }

  validateRequest(idClient: string, idRequest: string, status: string, uuid: { stage: string }): Observable<any> {
    return this.http.patch(`/clients/${idClient}/requests/${idRequest}/validate`, uuid, this.optionsHeader);
  }

  getCep(cep: string) {
    return this.http.get<ViaCep>(`/via-cep/` + cep, this.optionsHeader);
  }

  sendEmailOpenFinance(idClient: string) {
    return this.http.post<void>(`/open-finance/email/${idClient}`, this.optionsHeader);
  }

  getAllUsers() {
    return this.http.get<UserTransfer[]>(`/clients/transfer/users`, this.optionsHeader);
  }

  getClientsForTransfer(uuid: string) {
    return this.http.get<ListResponse<ClientTransfer[]>>(
      `/clients/transfer/${uuid}?total_page=999999`,
      this.optionsHeader
    );
  }

  transferClients(data: DiceTransfer) {
    return this.http.post<void>(`/clients/transfer`, data, this.optionsHeader);
  }

  getClientByDocument(idDocument: string) {
    return this.http.get<Client>(`/clients/by-document/${idDocument}`, this.optionsHeader);
  }

  openFinanceUpdate(data: { client_uuid: string; item_uuid: string }) {
    return this.http.put<{ client_uuid: string; item_uuid: string }>(
      `/open-finance/client/update-item`,
      data,
      this.optionsHeader
    );
  }

  allowContactClient(uuid: string, allow: string) {
    return this.http.patch<void>(`/clients/${uuid}/allow_contact?allow=${allow}`, null, this.optionsHeader);
  }

  uploadFile(data: FormData) {
    return this.http.post<string[]>("/uploads", data, this.optionsHeaderFile);
  }

  checkEmailExists(email: string) {
    return this.http.get<CheckEmail>(`/clients/by-email/${email}`, this.optionsHeader);
  }

  clientsStatus(uuid: string, status: string) {
    return this.http.patch<void>(`/clients/${uuid}/status?status=${status}`, this.optionsHeader);
  }
}
